import React, { useEffect, useMemo, useState } from 'react'
import {
    Badge,
    Box,
    Button,
    Code,
    Divider,
    HStack,
    Icon,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    UnorderedList,
    ListItem,
    VStack,
    useToast,
} from '@chakra-ui/react'
import {
    FaWandMagicSparkles,
    FaCircleCheck,
    FaArrowRight,
} from 'react-icons/fa6'
import {
    namedOperations,
    ProposedChangeOperation,
    useExecuteApprovedChangeMutation,
} from '~graphql/generated/graphql'
import type { ParsedProposal, ProposeChangeFieldDiff } from './propose-change'
import {
    loadAppliedProposalKeys,
    persistAppliedProposalKeys,
    proposalAppliedStorageKey,
} from './support-bot-local-state'

/**
 * Renders one `%= propose_change =%` block as an interactive approval card.
 *
 * The card is display-driven entirely by the parsed block's `display`
 * section; the "Apply" button calls `executeApprovedChange` with the
 * block's `execute` payload. The server re-validates that payload against
 * its allowlist and applies the change as the (real human) admin clicking
 * Apply — this card has no special authority, it's just a friendly trigger.
 *
 * Applied state is remembered in localStorage (keyed by conversation +
 * entity + patch) so a refresh doesn't resurrect a clickable Apply button.
 * Long diffs collapse behind "Show more" so a huge patch doesn't dominate
 * the thread.
 */
interface ProposeChangeCardProps {
    proposal: ParsedProposal
    /** Conversation that owns this message — scopes the Applied localStorage key. */
    conversationId: string | null
    /**
     * Fired after a change is applied successfully. Embedded surfaces (e.g.
     * the onboarding wizard) use this to refetch the lists the change
     * affected so the UI reflects the new state without a manual refresh.
     */
    onApplied?: () => void
}

/** Collapse the diff body when it would otherwise dominate the chat. */
const COLLAPSED_BODY_MAX_HEIGHT_PX = 220
/** Field-diff rows above this count force the collapse affordance. */
const COLLAPSE_AFTER_FIELD_COUNT = 4
/** Per-value preview length before an inline Expand is offered. */
const VALUE_PREVIEW_LENGTH = 120

export default function ProposeChangeCard({
    proposal,
    conversationId,
    onApplied,
}: ProposeChangeCardProps) {
    const toast = useToast()
    const { execute, display } = proposal

    const storageKey = useMemo(
        () =>
            conversationId
                ? proposalAppliedStorageKey(conversationId, execute)
                : null,
        [conversationId, execute],
    )

    const [applied, setApplied] = useState(() => {
        if (proposal.applied) return true
        return storageKey ? loadAppliedProposalKeys().has(storageKey) : false
    })
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [bodyExpanded, setBodyExpanded] = useState(false)

    // Re-sync if the conversation / proposal identity changes (e.g. switching chats).
    useEffect(() => {
        if (proposal.applied) {
            setApplied(true)
            return
        }
        if (!storageKey) {
            setApplied(false)
            return
        }
        setApplied(loadAppliedProposalKeys().has(storageKey))
    }, [storageKey, proposal.applied])

    const [executeApprovedChange, { loading }] =
        useExecuteApprovedChangeMutation()

    const bodyNeedsCollapse =
        display.field_diff.length > COLLAPSE_AFTER_FIELD_COUNT ||
        display.side_effects.length > 4 ||
        (display.summary?.length ?? 0) > 280 ||
        display.field_diff.some(
            d =>
                formatValue(d.before).length > VALUE_PREVIEW_LENGTH ||
                formatValue(d.after).length > VALUE_PREVIEW_LENGTH,
        )

    const isCreate = execute.operation === 'create'

    async function handleApply() {
        setErrorMessage(null)
        try {
            const result = await executeApprovedChange({
                variables: {
                    input: {
                        // The block parser yields a plain string union; map it
                        // onto the generated enum the mutation expects.
                        operation:
                            execute.operation === 'create'
                                ? ProposedChangeOperation.Create
                                : ProposedChangeOperation.Update,
                        target_entity_type: execute.target_entity_type,
                        target_entity_id: execute.target_entity_id ?? undefined,
                        clinic_id:
                            execute.clinic_id ?? display.clinic_id ?? undefined,
                        patch: execute.patch,
                        captured_before: execute.captured_before,
                    },
                },
                refetchQueries: getRefetchQueriesForProposal(
                    execute.target_entity_type,
                ),
                awaitRefetchQueries: true,
            })
            const payload = result.data?.executeApprovedChange
            if (payload?.success) {
                setApplied(true)
                if (storageKey) {
                    const next = loadAppliedProposalKeys()
                    next.add(storageKey)
                    persistAppliedProposalKeys(next)
                }
                toast({
                    title: isCreate ? 'Record created' : 'Change applied',
                    description: payload.message,
                    status: 'success',
                    duration: 6000,
                    isClosable: true,
                })
                onApplied?.()
            } else {
                throw new Error(
                    'The change did not apply. Re-run the investigation to propose a fresh change.',
                )
            }
        } catch (err) {
            const message =
                err instanceof Error ? err.message : 'Unknown error.'
            setErrorMessage(message)
            toast({
                title: isCreate ? 'Could not create' : 'Could not apply change',
                description: message,
                status: 'error',
                duration: 9000,
                isClosable: true,
            })
        }
    }

    return (
        <Box
            borderWidth={1}
            borderColor={applied ? 'green.300' : 'brand.300'}
            borderRadius="md"
            bg="white"
            my={3}
            overflow="hidden"
            maxW="100%"
            minW={0}
        >
            <HStack
                bg={applied ? 'green.50' : 'brand.50'}
                px={4}
                py={2}
                justify="space-between"
            >
                <HStack spacing={2} minW={0}>
                    <Icon
                        as={applied ? FaCircleCheck : FaWandMagicSparkles}
                        color={applied ? 'green.500' : 'brand.500'}
                    />
                    <Text fontSize="sm" fontWeight="bold" noOfLines={1}>
                        {applied
                            ? isCreate
                                ? 'Created'
                                : 'Applied'
                            : isCreate
                              ? 'Proposed new record'
                              : 'Proposed change'}
                    </Text>
                </HStack>
                <RiskBadge riskTier={display.risk_tier} />
            </HStack>

            <VStack align="stretch" spacing={3} px={4} py={3}>
                <Box>
                    <Text fontSize="sm" fontWeight="semibold">
                        {display.entity_display_name}
                    </Text>
                </Box>

                <Box
                    position="relative"
                    maxH={
                        bodyNeedsCollapse && !bodyExpanded
                            ? `${COLLAPSED_BODY_MAX_HEIGHT_PX}px`
                            : undefined
                    }
                    overflow={
                        bodyNeedsCollapse && !bodyExpanded
                            ? 'hidden'
                            : 'visible'
                    }
                >
                    <VStack align="stretch" spacing={3}>
                        {display.summary && (
                            <Text fontSize="sm" color="gray.700">
                                {display.summary}
                            </Text>
                        )}

                        {display.field_diff.length > 0 && (
                            <Box overflowX="auto">
                                <Table size="sm" variant="simple" fontSize="xs">
                                    <Thead bg="gray.100">
                                        <Tr>
                                            <Th>Field</Th>
                                            <Th>Change</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {display.field_diff.map(diff => (
                                            <FieldDiffRow
                                                key={diff.field}
                                                diff={diff}
                                                forceExpanded={bodyExpanded}
                                            />
                                        ))}
                                    </Tbody>
                                </Table>
                            </Box>
                        )}

                        {display.side_effects.length > 0 && (
                            <Box>
                                <Text
                                    fontSize="xs"
                                    fontWeight="semibold"
                                    color="gray.600"
                                >
                                    Side effects
                                </Text>
                                <UnorderedList
                                    fontSize="xs"
                                    color="gray.700"
                                    pl={4}
                                    mt={1}
                                >
                                    {display.side_effects.map((effect, i) => (
                                        <ListItem key={i}>{effect}</ListItem>
                                    ))}
                                </UnorderedList>
                            </Box>
                        )}
                    </VStack>

                    {bodyNeedsCollapse && !bodyExpanded && (
                        <Box
                            pointerEvents="none"
                            position="absolute"
                            left={0}
                            right={0}
                            bottom={0}
                            h="64px"
                            bgGradient="linear(to-t, white, transparent)"
                        />
                    )}
                </Box>

                {bodyNeedsCollapse && (
                    <Button
                        size="xs"
                        variant="ghost"
                        alignSelf="flex-start"
                        onClick={() => setBodyExpanded(v => !v)}
                    >
                        {bodyExpanded ? 'Show less' : 'Show more'}
                    </Button>
                )}

                {errorMessage && (
                    <Text fontSize="xs" color="red.500">
                        {errorMessage}
                    </Text>
                )}

                <Divider />

                <HStack justify="flex-end">
                    <Button
                        size="sm"
                        colorScheme={applied ? 'green' : 'brand'}
                        onClick={handleApply}
                        isLoading={loading}
                        isDisabled={applied}
                        leftIcon={
                            applied ? (
                                <FaCircleCheck />
                            ) : (
                                <FaWandMagicSparkles />
                            )
                        }
                    >
                        {applied
                            ? isCreate
                                ? 'Created'
                                : 'Applied'
                            : isCreate
                              ? 'Create record'
                              : 'Apply change'}
                    </Button>
                </HStack>
            </VStack>
        </Box>
    )
}

function FieldDiffRow({
    diff,
    forceExpanded,
}: {
    diff: ProposeChangeFieldDiff
    forceExpanded: boolean
}) {
    return (
        <Tr>
            <Td fontWeight="medium" verticalAlign="top">
                {diff.field}
            </Td>
            <Td>
                <HStack spacing={2} align="flex-start" wrap="wrap">
                    <ExpandableValue
                        value={diff.before}
                        colorScheme="red"
                        forceExpanded={forceExpanded}
                    />
                    <Icon
                        as={FaArrowRight}
                        color="gray.400"
                        boxSize={2.5}
                        mt={1}
                    />
                    <ExpandableValue
                        value={diff.after}
                        colorScheme="green"
                        forceExpanded={forceExpanded}
                    />
                </HStack>
            </Td>
        </Tr>
    )
}

function ExpandableValue({
    value,
    colorScheme,
    forceExpanded,
}: {
    value: unknown
    colorScheme: string
    forceExpanded: boolean
}) {
    const full = formatValue(value)
    const isLong = full.length > VALUE_PREVIEW_LENGTH
    const [expanded, setExpanded] = useState(false)
    const showFull = forceExpanded || expanded || !isLong
    const shown = showFull
        ? full
        : `${full.slice(0, VALUE_PREVIEW_LENGTH).trimEnd()}…`

    return (
        <Box maxW="100%">
            <Code
                fontSize="xs"
                colorScheme={colorScheme}
                whiteSpace="pre-wrap"
                wordBreak="break-word"
                display="inline-block"
            >
                {shown}
            </Code>
            {isLong && !forceExpanded && (
                <Button
                    size="xs"
                    variant="link"
                    ml={1}
                    verticalAlign="baseline"
                    onClick={() => setExpanded(v => !v)}
                >
                    {expanded ? 'Less' : 'More'}
                </Button>
            )}
        </Box>
    )
}

function RiskBadge({ riskTier }: { riskTier: string }) {
    const colorScheme =
        riskTier === 'high' ? 'red' : riskTier === 'medium' ? 'orange' : 'gray'
    return (
        <Badge colorScheme={colorScheme} variant="subtle" fontSize="2xs">
            {riskTier} risk
        </Badge>
    )
}

/**
 * Render an arbitrary JSON value for the diff cells. Primitives render
 * directly; objects/arrays are JSON-stringified. Truncation / expand is
 * handled by ExpandableValue — this always returns the full string.
 */
function formatValue(value: unknown): string {
    if (value === null || value === undefined) return '—'
    if (typeof value === 'string') return value.length > 0 ? value : '""'
    if (typeof value === 'boolean' || typeof value === 'number') {
        return String(value)
    }
    return JSON.stringify(value)
}

const CUSTOMER_SEGMENTS_ENTITY_TYPE = 'customer_segments'

/**
 * Ask Chckvet Apply does not go through the audience editor mutations, so
 * those queries would otherwise stay stale on the Audiences page. Apollo
 * only refetches queries that are currently active.
 */
function getRefetchQueriesForProposal(
    targetEntityType: string,
): string[] | undefined {
    if (targetEntityType !== CUSTOMER_SEGMENTS_ENTITY_TYPE) {
        return undefined
    }

    return [
        namedOperations.Query.queryCustomerSegments,
        namedOperations.Query.customerSegment,
        namedOperations.Query.getClientsByCustomerSegmentRPC,
        namedOperations.Query.entityLinksForClinic,
    ]
}
